import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Plus, Wallet, Calendar, Trash2, ChevronLeft, ChevronRight,
  Building2, Pill, User, Home,
} from 'lucide-react';
import { toast } from 'sonner';
import PropertyTab from '../../components/property/PropertyTab';
import {
  DonutBreakdown, TrendChart, Sparkline,
  PALETTE, formatINR, formatCompact,
} from '../../components/traders/ExpenseCharts';

const SummaryCard = ({ id, icon: Icon, label, value, sub, color, active, onClick, spark }) => (
  <button
    type="button"
    data-testid={`summary-card-${id}`}
    onClick={onClick}
    className={`group text-left rounded-xl border bg-white px-3 py-3 transition-all hover:shadow-sm hover:-translate-y-[1px] ${
      active ? 'border-zinc-900 ring-1 ring-zinc-900/10' : 'border-zinc-200'
    }`}
  >
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5">
        <span
          className="w-5 h-5 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${color}1a`, color }}
        >
          <Icon className="h-3 w-3" />
        </span>
        <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide truncate">{label}</span>
      </div>
      {sub != null && (
        <span className={`text-[10px] font-medium tabular-nums ${sub >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
          {sub >= 0 ? '▲' : '▼'} {Math.abs(sub).toFixed(0)}%
        </span>
      )}
    </div>
    <div className="flex items-end justify-between gap-2">
      <span className="text-lg font-semibold tabular-nums text-zinc-900 leading-tight">
        {formatCompact(value)}
      </span>
      {spark && <Sparkline data={spark} color={color} />}
    </div>
  </button>
);

const EXPENSE_CATEGORIES = [
  { value: 'traders',  label: 'Shri Kripa Traders', icon: Building2, color: PALETTE.traders  },
  { value: 'pharmacy', label: 'Pharmacy',           icon: Pill,      color: PALETTE.pharmacy },
  { value: 'ayush',    label: 'Ayush',              icon: User,      color: PALETTE.ayush    },
  { value: 'bharti',   label: 'Bharti',             icon: User,      color: PALETTE.bharti   },
];

// Build YYYY-MM key locally (timezone-safe — never use toISOString here)
const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (key, delta) => {
  const [y, m] = key.split('-').map(Number);
  const total = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
};

const formatMonthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const ExpenseModule = () => {
  const [expenses, setExpenses] = useState([]);
  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState([]); // last 12 months series
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
  const [filterCategory, setFilterCategory] = useState('all');

  const [newExpense, setNewExpense] = useState({
    date: (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })(),
    amount: '',
    category: '',
    remark: '',
  });

  useEffect(() => { fetchData(); }, [selectedMonth, filterCategory]);

  const fetchData = async () => {
    try {
      const categoryParam = filterCategory !== 'all' ? `&category=${filterCategory}` : '';
      const [expensesRes, reportRes, trendRes] = await Promise.all([
        axios.get(`${API}/expenses?month=${selectedMonth}${categoryParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/expenses/report?month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/expenses/trend?months=12`, { headers: getAuthHeaders() }),
      ]);
      setExpenses(expensesRes.data);
      setReport(reportRes.data);
      setTrend(trendRes.data?.series || []);
    } catch (error) {
      toast.error(handleApiError(error, 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
      }, { headers: getAuthHeaders() });
      toast.success('Expense added');
      setShowAdd(false);
      const d = new Date();
      setNewExpense({
        date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        amount: '', category: '', remark: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${id}`, { headers: getAuthHeaders() });
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete expense';
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete expense');
    }
  };

  // ---- Derived data ----
  const monthName = formatMonthLabel(selectedMonth);

  // Previous month report (last entry before selectedMonth in trend), used for delta calc.
  const trendUpTo = useMemo(() => {
    // The trend endpoint is rolling 12-month from today — for delta we use trend if it contains both selectedMonth and the prior one.
    const idx = trend.findIndex(t => t.month === selectedMonth);
    return { idx, series: trend };
  }, [trend, selectedMonth]);

  const overallTotal = report?.overall_total || 0;
  const prevTotal = useMemo(() => {
    if (trendUpTo.idx > 0) return trendUpTo.series[trendUpTo.idx - 1].total;
    return 0;
  }, [trendUpTo]);

  const overallDelta = prevTotal > 0 ? ((overallTotal - prevTotal) / prevTotal) * 100 : 0;
  const overallUp = overallDelta >= 0;

  // Per-category sparkline data: last 6 months
  const sparkData = useMemo(() => {
    const tail = trend.slice(-6);
    const make = (key) => tail.map(t => ({ v: t.category_totals?.[key] || 0 }));
    return {
      all:      tail.map(t => ({ v: t.total || 0 })),
      traders:  make('traders'),
      pharmacy: make('pharmacy'),
      ayush:    make('ayush'),
      bharti:   make('bharti'),
    };
  }, [trend]);

  const getCategoryInfo = (v) => EXPENSE_CATEGORIES.find(c => c.value === v) || { label: v, color: '#64748b', icon: Wallet };

  // ---- Render ----
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96 text-zinc-400 text-sm">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-heading tracking-tight text-zinc-900">Expenses</h1>
            <p className="text-sm text-zinc-500 mt-0.5">Track and manage expenses &amp; properties</p>
          </div>
        </div>

        <Tabs defaultValue="expenses" className="w-full">
          <TabsList className="grid grid-cols-2 max-w-xs" data-testid="expense-module-tabs">
            <TabsTrigger value="expenses" data-testid="tab-add-expense">
              <Wallet className="h-4 w-4 mr-1.5" /> Expenses
            </TabsTrigger>
            <TabsTrigger value="property" data-testid="tab-property">
              <Home className="h-4 w-4 mr-1.5" /> Property
            </TabsTrigger>
          </TabsList>

          <TabsContent value="expenses" className="mt-5 space-y-5">
            {/* Toolbar: month selector + add button */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="inline-flex items-center rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="month-selector">
                <button
                  data-testid="month-prev"
                  onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))}
                  className="px-2 py-1.5 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="px-3 py-1.5 border-x border-zinc-200 flex items-center gap-2 min-w-[170px] justify-center">
                  <Calendar className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-900 tabular-nums" data-testid="month-label">
                    {monthName}
                  </span>
                </div>
                <button
                  data-testid="month-next"
                  onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))}
                  className="px-2 py-1.5 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-expense-btn"
                    className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white text-sm shadow-sm"
                  >
                    <Plus className="h-4 w-4 mr-1.5" /> Add Expense
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-heading tracking-tight">Add Expense</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={newExpense.date}
                        onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                        required
                        className="h-11"
                        data-testid="expense-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount (₹) *</Label>
                      <Input
                        type="number"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                        placeholder="0.00"
                        required
                        className="h-11"
                        min="0"
                        step="0.01"
                        data-testid="expense-amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={newExpense.category} onValueChange={(val) => setNewExpense({ ...newExpense, category: val })}>
                        <SelectTrigger className="h-11" data-testid="expense-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPENSE_CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>
                              <div className="flex items-center gap-2">
                                <cat.icon className="h-4 w-4" />
                                {cat.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Remark</Label>
                      <Textarea
                        value={newExpense.remark}
                        onChange={(e) => setNewExpense({ ...newExpense, remark: e.target.value })}
                        placeholder="Add notes…"
                        rows={3}
                        data-testid="expense-remark"
                      />
                    </div>
                    <Button type="submit" className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="expense-submit">
                      Add Expense
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Summary cards row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
              <SummaryCard
                id="all"
                icon={Wallet}
                label="Total"
                value={overallTotal}
                sub={prevTotal > 0 ? overallDelta : null}
                color="#0f172a"
                active={filterCategory === 'all'}
                onClick={() => setFilterCategory('all')}
                spark={sparkData.all}
              />
              {EXPENSE_CATEGORIES.map(cat => {
                const value = report?.category_totals?.[cat.value] || 0;
                const idx = trend.findIndex(t => t.month === selectedMonth);
                const prev = idx > 0 ? (trend[idx - 1].category_totals?.[cat.value] || 0) : 0;
                const delta = prev > 0 ? ((value - prev) / prev) * 100 : null;
                return (
                  <SummaryCard
                    key={cat.value}
                    id={cat.value}
                    icon={cat.icon}
                    label={cat.label}
                    value={value}
                    sub={delta}
                    color={cat.color}
                    active={filterCategory === cat.value}
                    onClick={() => setFilterCategory(filterCategory === cat.value ? 'all' : cat.value)}
                    spark={sparkData[cat.value]}
                  />
                );
              })}
            </div>

            {/* Overview: donut + trend */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
              <div className="lg:col-span-2">
                <DonutBreakdown report={report} expenses={expenses} />
              </div>
              <div className="lg:col-span-3">
                <TrendChart series={trend} />
              </div>
            </div>

            {/* Filter pill row (compact) */}
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="category-filter-pills">
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  filterCategory === 'all'
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {EXPENSE_CATEGORIES.map(cat => {
                const active = filterCategory === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setFilterCategory(active ? 'all' : cat.value)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      active
                        ? 'text-white border-transparent'
                        : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                    }`}
                    style={active ? { backgroundColor: cat.color } : {}}
                    data-testid={`filter-${cat.value}`}
                  >
                    <cat.icon className="h-3 w-3" />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Expense list */}
            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="expense-list">
              <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">
                    Entries
                  </span>
                  <span className="text-[11px] text-zinc-500 tabular-nums">
                    {expenses.length} {expenses.length === 1 ? 'entry' : 'entries'} · {monthName}
                  </span>
                </div>
                <span className="text-sm font-semibold tabular-nums text-zinc-900">
                  {formatINR(
                    filterCategory !== 'all'
                      ? expenses.reduce((s, e) => s + e.amount, 0)
                      : overallTotal
                  )}
                </span>
              </div>

              {expenses.length === 0 ? (
                <div className="p-10 text-center">
                  <Wallet className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
                  <p className="text-sm text-zinc-500 mb-3">
                    {filterCategory !== 'all'
                      ? `No ${getCategoryInfo(filterCategory).label} expenses for ${monthName}`
                      : `No expenses recorded for ${monthName}`}
                  </p>
                  <Button onClick={() => setShowAdd(true)} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white">
                    <Plus className="h-4 w-4 mr-1.5" /> Add First Expense
                  </Button>
                </div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {expenses.map(expense => {
                    const cat = getCategoryInfo(expense.category);
                    const CatIcon = cat.icon;
                    return (
                      <li
                        key={expense.id}
                        className="px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-50/60 transition-colors"
                        data-testid={`expense-row-${expense.id}`}
                      >
                        <span
                          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${cat.color}1a`, color: cat.color }}
                        >
                          <CatIcon className="h-3.5 w-3.5" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-medium text-zinc-700">{cat.label}</span>
                            {expense.subcategory && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-zinc-100 text-zinc-600">
                                {expense.subcategory}
                              </span>
                            )}
                            {expense.ac_statement_id && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700"
                                title="Auto-synced from Pharmacy A/C Statement"
                                data-testid={`expense-synced-badge-${expense.id}`}
                              >
                                ↻ Synced
                              </span>
                            )}
                            <span className="text-[11px] text-zinc-400 tabular-nums">
                              {new Date(expense.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                            </span>
                          </div>
                          {expense.remark && (
                            <p className="text-xs text-zinc-500 truncate mt-0.5">{expense.remark}</p>
                          )}
                        </div>
                        <span className="text-sm font-semibold tabular-nums text-zinc-900 ml-2">
                          {formatINR(expense.amount)}
                        </span>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          disabled={!!expense.ac_statement_id}
                          className="text-zinc-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed p-1.5 rounded-md transition-colors"
                          title={expense.ac_statement_id ? 'Auto-synced — delete from Pharmacy A/C Statement' : 'Delete'}
                          data-testid={`expense-delete-${expense.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </TabsContent>

          <TabsContent value="property" className="mt-5">
            <PropertyTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ExpenseModule;
